import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { allPages } from '../../redux/pagesSlice';
import { saveAs } from 'file-saver';
import {
	Button,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { UilArrowToBottom } from '@iconscout/react-unicons';
import components from '../../utils/componentsDictionary';
import raw from 'raw.macro';
import mergeSass from 'merge-sass';
import { getAuthHeader } from '../../utils/auth-token';

function getNowDate() {
	return new Date().toISOString().split('T')[0];
}

const errorDict = {
	'--transparent': 'transparent',
	'--text-color-pure': '$text-color-pure',
	'--brand-primary-color-pure': '$brand-color-primary-pure',
};

const getClassesToReplace = (item, classes) => {
	//item is the component, with maybe props and maybe children components
	//we have to look for the prop blockClass in the component and in its children recursively

	//if the component has a blockClass prop, we replace the blockClass with ${blockClass}-${key}
	if (item.props?.blockClass) {
		if (Array.isArray(item.props.blockClass)) {
			item.props.blockClass.forEach((blockClass, index) => {
				classes.push(blockClass);
			});
		} else {
			classes.push(item.props.blockClass);
		}
	}
	return replaceBlockClassRecursively(item, classes);
};

const replaceBlockClassRecursively = (item, classes) => {
	item?.childrenComponents?.forEach((child) => {
		if (child.props?.blockClass) {
			if (Array.isArray(child.props.blockClass)) {
				child.props.blockClass.forEach((blockClass, index) => {
					classes.push(blockClass);
				});
			} else {
				classes.push(child.props.blockClass);
			}
		}
		replaceBlockClassRecursively(child, classes);
	});
	item?.blocks?.forEach((child) => {
		if (child.props?.blockClass) {
			if (Array.isArray(child.props.blockClass)) {
				child.props.blockClass.forEach((blockClass, index) => {
					classes.push(blockClass);
				});
			} else {
				classes.push(child.props.blockClass);
			}
		}
		replaceBlockClassRecursively(child, classes);
	});
	//slots
	const props = Object.keys(item?.props || {});
	if (props.length) {
		props.forEach((prop) => {
			if (item.props[prop]?.componentName) {
				replaceBlockClassRecursively(item.props[prop], classes);
			}
			if (item?.props[props]?.props?.blockClass) {
				classes.push(item.props[props].props.blockClass);
			}
		});
	}
};

const replaceVariables = (scss, variables, tokens, offsetKey, item) => {
	let classes = [];
	getClassesToReplace(item, classes);
	//remove dupes
	if (offsetKey && offsetKey !== 0) {
		classes = [...new Set(classes)].filter((c) => c);
		classes.forEach((blockClass) => {
			scss = scss.replace(new RegExp(`\\&\\-\\-${blockClass}\\b`, 'gi'), `&--${blockClass}-${offsetKey}`);
		});
	}
	variables?.forEach(({ list }) => {
		list.forEach(({ from, to }) => {
			const replaceRegex = new RegExp(`var\\(${to}\\)`, 'gi');
			let found = false;
			Object.keys(tokens).forEach((tokenFile) => {
				if (tokens[tokenFile].includes(from)) {
					found = true;
					if (!isNaN(from)) {
						scss = scss.replace(replaceRegex, from);
						return;
					}

					const regex = new RegExp(`(\\$([\\w]-*)*):\\s*var\\(${from}\\)`, 'gi');
					const matchRegex = tokens[tokenFile].match(regex);
					let sassVar;

					if (errorDict[from]) {
						sassVar = errorDict[from];
					} else {
						try {
							sassVar = matchRegex[0].split(':')[0];
						} catch (e) {
							console.error(e);
						}
					}

					if (!sassVar) {
						console.warn(`Não foi encontrada a variável de Sass correspondente a ${from}`);
						return;
					}
					scss = scss.replace(replaceRegex, sassVar);
				}
			});
			if (!found) {
				console.warn(`A variável ${from} não foi encontrada. Verifique o nome da variável`);
			}
		});
	});

	return scss;
};

const SendToCompileBtn = ({ viewId, accountName }) => {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const toast = useToast();
	const [tokens, setTokens] = React.useState();
	const [isBtnDisabled, setIsBtnDisabled] = React.useState(false);
	const [appCss, setAppCss] = React.useState();
	const [checkoutHtml, setCheckoutHtml] = React.useState();
	const [checkoutCustom, setCheckoutCustom] = React.useState();
	const [typeOfCheckout, setTypeOfCheckout] = React.useState(null);
	const pages = useSelector(allPages);
	const [finalPages, setFinalPages] = React.useState();

	React.useEffect(() => {
		if (pages) {
			setFinalPages(JSON.parse(JSON.stringify(pages)));
		}
	}, [pages]);

	React.useEffect(() => {
		setAppCss(null);
	}, []);

	const getTemplate = async (scssFile, templateName) => {
		const files = {};

		//this dictionary is used to avoid duplicate imports
		const templateDict = {};
		const newPages = JSON.parse(JSON.stringify(pages));
		await asyncMap(newPages, templateDict, scssFile, templateName, files);

		// await Promise.all(promises)
		return files;
	};

	const asyncMap = async (newPages, templateDict, scssFile, templateName, files) => {
		await Promise.all(
			newPages.map(async (page) => {
				await Promise.all(
					page?.pageData.map(async (item) => {
						if (!templateName.includes(item.componentGroup)) {
							return item;
						}
						if (Object.keys(item).length <= 3) {
							return item;
						}
						try {
							const group = item.componentGroup === 'Breadcrumb' ? 'BreadcrumbGroup' : item.componentGroup;
							const indexKey = `${group}/${scssFile}`;
							const dictKey = `${indexKey}/${item.templateId ?? 1}`;
							const { default: scss } = await import(
								`!!raw-loader!../../templates/components/${group}/${item.templateId ?? 1}/${scssFile}`
							);
							let newScss;
							if (item?.variables?.length && scss) {
								newScss = replaceVariables(scss, item.variables, tokens, templateDict[dictKey], item);
							}
							//aqui tem que ser 2 mesmo, para a contagem ignorar o primeiro, e colocar a partir do segundo
							if (!templateDict[dictKey]) {
								templateDict[dictKey] = 2;
							} else {
								//++
								templateDict[dictKey] = templateDict[dictKey] + 1;
							}

							if (files[indexKey]) {
								files[indexKey] = mergeSass(files[indexKey], newScss ? newScss : scss);
							} else {
								files[indexKey] = newScss ? newScss : scss;
							}

							return item;
						} catch (error) {
							console.warn(`Não achou o arquivo do grupo ${item.componentGroup}`, error);
							return item;
						}
					}),
				);
			}),
		);
		return newPages;
	};

	const changeDuplicatedBlockClass = () => {
		let newPages = JSON.parse(JSON.stringify(pages));
		const templateDict = {};
		newPages = newPages.map((page) => {
			page.pageData = page.pageData.map((item) => {
				const group = item.componentGroup === 'Breadcrumb' ? 'BreadcrumbGroup' : item.componentGroup;
				const indexKey = `${group}`;
				const dictKey = `${indexKey}/${item.templateId ?? 1}`;
				let newItem = JSON.stringify(item);
				if (!templateDict[dictKey]) {
					templateDict[dictKey] = 1;
				} else {
					//++
					templateDict[dictKey] = templateDict[dictKey] + 1;
					let classes = [];
					getClassesToReplace(item, classes);
					classes = [...new Set(classes)].filter((c) => c);
					classes.forEach((blockClass) => {
						newItem = newItem.replace(
							new RegExp(`\\b${blockClass}\\b`, 'gi'),
							`${blockClass}-${templateDict[dictKey]}`,
						);
					});
				}
				return JSON.parse(newItem);
			});
			return page;
		});
		setFinalPages(newPages);
	};

	const getHeader = async (checkoutLogoUrl) => {
		const header = await raw(`../../templates/vtex-apps/apps/checkout/html/header.html`);
		return header.replace('{{checkoutHeaderImage}}', checkoutLogoUrl);
	};

	const getFooter = async () => {
		return await raw(`../../templates/vtex-apps/apps/checkout/html/footer.html`);
	};

	const vtexCheckoutHtml = async () => {
		setCheckoutHtml({
			footer: await getFooter(),
			header: await getHeader('/arquivos/logo-checkout.svg'),
		});
	};

	const vtexCheckoutCustom = async () => {
		const js = await raw(`../../templates/vtex-apps/apps/checkout/checkout6-custom.js`);
		setCheckoutCustom(js);
	};

	const getTokens = () => {
		setTokens({
			'_base-font.scss': raw('../../templates/vtex-apps/apps/tokens/_base-font.scss'),
			'_colors.scss': raw('../../templates/vtex-apps/apps/tokens/_colors.scss'),
			'_container.scss': raw('../../templates/vtex-apps/apps/tokens/_container.scss'),
			'_effects.scss': raw('../../templates/vtex-apps/apps/tokens/_effects.scss'),
			'_fonts.scss': raw('../../templates/vtex-apps/apps/tokens/_fonts.scss'),
			'_formats.scss': raw('../../templates/vtex-apps/apps/tokens/_formats.scss'),
			'_media.scss': raw('../../templates/vtex-apps/apps/tokens/_media.scss'),
			'_opacities.scss': raw('../../templates/vtex-apps/apps/tokens/_opacities.scss'),
			'_shadows.scss': raw('../../templates/vtex-apps/apps/tokens/_shadows.scss'),
			'_spacing.scss': raw('../../templates/vtex-apps/apps/tokens/_spacing.scss'),
			'_typography.scss': raw('../../templates/vtex-apps/apps/tokens/_typography.scss'),
			'_icons.scss': raw('../../templates/vtex-apps/apps/tokens/_icons.scss'),
		});
	};

	const vtexAppCss = async (checkoutFiles) => {
		const css = {
			components: {
				'checkout6-custom.scss': {
					checkout: checkoutFiles,
				},
				'vtex.breadcrumb.scss': await getTemplate('_breadcrumb.scss', ['BreadcrumbGroup', 'Breadcrumb']),

				'vtex.login.scss': await getTemplate('_login.scss', ['HeaderDesktop', 'HeaderMobile']),
				'vtex.menu.scss': await getTemplate('_menu.scss', [
					'StoreMenuDesktop',
					'StoreMenuMobile',
					'HeaderDesktop',
					'HeaderMobile',
					'FooterDesktop',
					'FooterMobile',
					'InstitutionalGroup',
				]),

				'vtex.flex-layout.scss': await getTemplate('_flex-layout.scss', [
					'RichTextRow',
					'VideoGroup',
					'SearchResultLayoutMobile',
					'SearchResultLayoutDesktop',
					'ProductSummaryList',
					'InstitutionalGroup',
					'SliderLayout',
					'SearchNotFoundGroup',
					'ReviewsGroup',
					'MinicartGroup',
					'ModalNewsletter',
					'ProductDescriptionGroup',
					'HeaderDesktop',
					'HeaderMobile',
					'FooterDesktop',
					'FooterMobile',
					'Newsletter',
					'BannerSectionGroup',
					'TipbarGroup',
					'RelatedProducts',
					'ProductSummaryList',
					'ProductSummaryShelf',
					'ProductImagesDetailsGroup',
					'ProductSpecificationSection',
					'BrandGroup',
					'SearchResultLayoutDesktop',
					'SearchResultLayoutMobile',
					'BreadcrumbGroup',
					'Breadcrumb',
					'MinicartGroup',
					'BuyTogetherGroup',
					'RichTextVideo',
					'StoreMenuDesktop',
					'StoreMenuMobile',
				]),

				'vtex.minicart.scss': await getTemplate('_minicart.scss', ['MinicartGroup']),
				'vtex.product-highlights.scss': await getTemplate('_highlights.scss', [
					'ProductImagesDetailsGroup',
					'ProductSummaryShelf',
					'ProductSummaryShelfComponent',
				]),
				'vtex.product-identifier.scss': await getTemplate('_identifier.scss', ['ProductImagesDetailsGroup']),
				'vtex.product-price.scss': await getTemplate('_product-price.scss', [
					'ProductImagesDetailsGroup',
					'RelatedProducts',
					'ProductSummaryList',
					'ProductSummaryShelf',
					'ProductSummaryShelfComponent',
				]),
				'vtex.product-quantity.scss': await getTemplate('_quantity.scss', ['ProductImagesDetailsGroup']),
				'vtex.product-specifications.scss': await getTemplate('_product-specifications.scss', [
					'ProductSpecificationSection',
				]),
				'vtex.product-summary.scss': await getTemplate('_product-summary.scss', [
					'RelatedProducts',
					'ProductSummaryList',
					'ProductSummaryShelf',
					'ProductSummaryShelfComponent',
				]),
				'vtex.rich-text.scss': await getTemplate('_rich-text.scss', [
					'ReviewsGroup',
					'RichTextRow',
					'ProductSummaryShelf',
					'ProductSummaryShelfComponent',
					'ProductImagesDetailsGroup',
					'InstitutionalGroup',
					'BannerRichText',
					'ProductSpecificationSection',
					'RelatedProducts',
					'MinicartGroup',
					'ModalNewsletter',
					'HeaderDesktop',
					'HeaderMobile',
					'FooterDesktop',
					'FooterMobile',
					'Newsletter',
					'TipbarGroup',
					'ProductSummaryList',
					'BrandGroup',
					'RichTextVideo',
					'BuyTogetherGroup',
				]),
				'vtex.shelf.scss': await getTemplate('_shelf.scss', ['RelatedProducts']),
				'vtex.slider-layout.scss': await getTemplate('_slider-layout.scss', [
					'TipbarGroup',
					'HeaderMobile',
					'RelatedProducts',
					'SliderLayout',
					'ProductSummaryList',
					'BrandGroup',
					'BuyTogetherGroup',
				]),
				'vtex.stack-layout.scss': await getTemplate('_stack-layout.scss', [
					'ProductSummaryList',
					'ProductSummaryShelf',
					'ProductSummaryShelfComponent',
				]),
				'vtex.sticky-layout.scss': await getTemplate('_sticky-layout.scss', ['HeaderDesktop', 'HeaderMobile']),
				'vtex.store-components.scss': await getTemplate('_store-components.scss', [
					'InstitutionalGroup',
					'ProductSpecificationSection',
					'ModalNewsletter',
					'HeaderDesktop',
					'HeaderMobile',
					'ProductDescriptionGroup',
					'ProductImagesDetailsGroup',
					'ProductSummaryShelf',
					'ProductSummaryShelfComponent',
					'BrandGroup',
					'BannerSectionGroup',
					'SliderLayout',
					'Newsletter',
				]),
				'vtex.store-drawer.scss': await getTemplate('_store-drawer.scss', ['MinicartGroup', 'HeaderMobile']),
				'vtex.store-footer.scss': await getTemplate('_footer.scss', ['FooterDesktop']),
				'vtex.store-newsletter.scss': await getTemplate('_store-newsletter.scss', [
					'ModalNewsletter',
					'Newsletter',
					'FooterDesktop',
					'FooterMobile',
				]),
				'vtex.modal-layout.scss': await getTemplate('_modal-layout.scss', [
					'ModalNewsletter',
					'HeaderDesktop',
					'HeaderMobile',
					'ProductImagesDetailsGroup',
				]),
				'vtex.disclosure-layout.scss': await getTemplate('_disclosure-layout.scss', [
					'ProductSpecificationSection',
					'FooterMobile',
					'HeaderDesktop',
					'StoreMenuMobile',
					'InstitutionalGroup',
				]),
				'vtex.wish-list.scss': await getTemplate('_wish-list.scss', [
					'ProductImagesDetailsGroup',
					'ProductSummaryShelf',
					'ProductSummaryShelfComponent',
				]),
				'vtex.reviews-and-ratings.scss': await getTemplate('_reviews-and-ratings.scss', [
					'ReviewsGroup',
					'ProductSummaryShelf',
					'ProductSummaryShelfComponent',
					'ProductImagesDetailsGroup',
				]),
				'vtex.tab-layout.scss': await getTemplate('_tab-layout.scss', [
					'ProductSummaryList',
					'ProductImagesDetailsGroup',
				]),
				'vtex.store-link.scss': await getTemplate('_store-link.scss', [
					'ProductSummaryList',
					'FooterDesktop',
					'ProductImagesDetailsGroup',
				]),
				'vtex.info-card-list.scss': await getTemplate('_info-card-list.scss', ['ProductSummaryList']),
				'vtex.product-list.scss': await getTemplate('_product-list.scss', ['MinicartGroup']),
				'vtex.search-result.scss': await getTemplate('_search-result.scss', [
					'SearchNotFoundGroup',
					'SearchResultLayoutDesktop',
					'SearchResultLayoutMobile',
				]),
				'vtex.checkout-summary.scss': await getTemplate('_checkout-summary.scss', ['MinicartGroup']),
				'vtexeurope.minicart-freeshipping-bar.scss': await getTemplate('_freeshipping-bar.scss', ['MinicartGroup']),
				'vtex.availability-notify.scss': await getTemplate('_availability-notify.scss', ['ProductImagesDetailsGroup']),
				'vtex.product-gifts.scss': await getTemplate('_product-gifts.scss', ['ProductImagesDetailsGroup']),
				'vtex.overlay-layout.scss': await getTemplate('_overlay-layout.scss', ['HeaderDesktop']),
				'vtex.store-video.scss': await getTemplate('_video.scss', ['VideoGroup', 'RichTextVideo']),
				'maeztraio.buy-together.scss': await getTemplate('_buy-together.scss', ['BuyTogetherGroup']),
				'vtex.list-context.scss': await getTemplate('_list-context.scss', [
					'BuyTogetherGroup',
					'BannerSectionGroup',
					'Newsletter',
					'FooterDesktop',
					'FooterMobile',
				]),
				'vtex.seller-selector.scss': await getTemplate('_seller-selector.scss', ['ProductImagesDetailsGroup']),
				'vtex.mega-menu.scss': await getTemplate('_mega-menu.scss', ['StoreMenuDesktop', 'StoreMenuMobile']),
				'maeztraio.cookie-modal.scss': {
					'cookies-modal': {
						'_cookie-modal.scss': raw(`../../templates/vtex-apps/apps/components/cookies/_cookie-modal.scss`),
					},
				},
				'vtex.my-account.scss': {
					account: {
						'_address.scss': raw(`../../templates/vtex-apps/apps/components/account/_address.scss`),
						'_general.scss': raw(`../../templates/vtex-apps/apps/components/account/_general.scss`),
						'_header.scss': raw(`../../templates/vtex-apps/apps/components/account/_header.scss`),
						'_menu.scss': raw(`../../templates/vtex-apps/apps/components/account/_menu.scss`),
						'_orders.scss': raw(`../../templates/vtex-apps/apps/components/account/_orders.scss`),
						'_payment-cards.scss': raw(`../../templates/vtex-apps/apps/components/account/_payment-cards.scss`),
						'_profile.scss': raw(`../../templates/vtex-apps/apps/components/account/_profile.scss`),
						'_vtex.login.scss': raw(`../../templates/vtex-apps/apps/components/account/_vtex.login.scss`),
					},
				},
			},
			mixins: {
				'_button.scss': raw('../../templates/vtex-apps/apps/mixins/_button.scss'),
				'_flag.scss': raw('../../templates/vtex-apps/apps/mixins/_flag.scss'),
				'_flex.scss': raw('../../templates/vtex-apps/apps/mixins/_flex.scss'),
				'_heading.scss': raw('../../templates/vtex-apps/apps/mixins/_heading.scss'),
				'_headings.scss': raw('../../templates/vtex-apps/apps/mixins/_headings.scss'),
				'_icon-button.scss': raw('../../templates/vtex-apps/apps/mixins/_icon-button.scss'),
				'_input.scss': raw('../../templates/vtex-apps/apps/mixins/_input.scss'),
				'_links.scss': raw('../../templates/vtex-apps/apps/mixins/_links.scss'),
				'_overflow.scss': raw('../../templates/vtex-apps/apps/mixins/_overflow.scss'),
				'_paragraph.scss': raw('../../templates/vtex-apps/apps/mixins/_paragraph.scss'),
				'_scrollbar.scss': raw('../../templates/vtex-apps/apps/mixins/_scrollbar.scss'),
				'_text.scss': raw('../../templates/vtex-apps/apps/mixins/_text.scss'),
				'_responsive.scss': raw('../../templates/vtex-apps/apps/mixins/_responsive.scss'),
			},
			extends: {
				'_container.scss': raw('../../templates/vtex-apps/apps/extends/_container.scss'),
				'_slider-arrows.scss': raw('../../templates/vtex-apps/apps/extends/_slider-arrows.scss'),
			},
			placeholders: {
				'_animations.scss': raw('../../templates/vtex-apps/apps/placeholders/_animations.scss'),
				'_arrow.scss': raw('../../templates/vtex-apps/apps/placeholders/_arrow.scss'),
				'_bullet.scss': raw('../../templates/vtex-apps/apps/placeholders/_bullet.scss'),
				'_buttons.scss': raw('../../templates/vtex-apps/apps/placeholders/_buttons.scss'),
				'_collection-item.scss': raw('../../templates/vtex-apps/apps/placeholders/_collection-item.scss'),
				'_container.scss': raw('../../templates/vtex-apps/apps/placeholders/_container.scss'),
				'_dots.scss': raw('../../templates/vtex-apps/apps/placeholders/_dots.scss'),
				'_flags.scss': raw('../../templates/vtex-apps/apps/placeholders/_flags.scss'),
				'_icon-buttons.scss': raw('../../templates/vtex-apps/apps/placeholders/_icon-buttons.scss'),
				'_import-placeholders.scss': raw('../../templates/vtex-apps/apps/placeholders/_import-placeholders.scss'),
				'_inputs.scss': raw('../../templates/vtex-apps/apps/placeholders/_inputs.scss'),
				'_links.scss': raw('../../templates/vtex-apps/apps/placeholders/_links.scss'),
				'_subtitles.scss': raw('../../templates/vtex-apps/apps/placeholders/_subtitles.scss'),
				'_titles.scss': raw('../../templates/vtex-apps/apps/placeholders/_titles.scss'),
				helpers: {
					'_btn-reset.scss': raw('../../templates/vtex-apps/apps/placeholders/helpers/_btn-reset.scss'),
					'_centering.scss': raw('../../templates/vtex-apps/apps/placeholders/helpers/_centering.scss'),
					'_image-set.scss': raw('../../templates/vtex-apps/apps/placeholders/helpers/_image-set.scss'),
					'_text-reset.scss': raw('../../templates/vtex-apps/apps/placeholders/helpers/_text-reset.scss'),
				},
			},
			'_variables.scss': raw('../../templates/vtex-apps/apps/_variables.scss'),
			'_mixins.scss': raw('../../templates/vtex-apps/apps/_mixins.scss'),
			'_extends.scss': raw('../../templates/vtex-apps/apps/_extends.scss'),
		};
		setAppCss(css);
	};

	const compileProject = () => {
		changeDuplicatedBlockClass();
		setTokens(null);
		setCheckoutCustom(null);
		setCheckoutHtml(null);
		getTokens();
		vtexCheckoutHtml();
		vtexCheckoutCustom();
	};

	React.useEffect(() => {
		setAppCss(null);
		if (tokens && !typeOfCheckout) {
			console.log('compilando projeto');
		}
		if (tokens && typeOfCheckout) {
			let checkoutFiles;
			if (typeOfCheckout === 'default') {
				checkoutFiles = {
					'_structure.scss': raw(`../../templates/vtex-apps/apps/checkout/_structure.scss`),
					'_buttons.scss': raw(`../../templates/vtex-apps/apps/checkout/_buttons.scss`),
					'_common.scss': raw(`../../templates/vtex-apps/apps/checkout/_common.scss`),
					'_mobile-fix.scss': raw(`../../templates/vtex-apps/apps/checkout/_mobile-fix.scss`),
					'_shipping.scss': raw(`../../templates/vtex-apps/apps/checkout/_shipping.scss`),
					'_step.scss': raw(`../../templates/vtex-apps/apps/checkout/_step.scss`),
					'_container.scss': raw(`../../templates/vtex-apps/apps/checkout/_container.scss`),
					'_totalizers.scss': raw(`../../templates/vtex-apps/apps/checkout/_totalizers.scss`),
				};
			} else {
				checkoutFiles = {
					'_common.scss': raw(`../../templates/vtex-apps/apps/checkout-w-ui-custom/_common.scss`),
				};
			}
			vtexAppCss(checkoutFiles);
		}
	}, [tokens, typeOfCheckout]);

	function getAllComponentsNameRecursive(pages) {
		const componentsName = [];
		pages.forEach((page) => {
			page.pageData.forEach((component) => {
				componentsName.push(component.componentName);
				if (component?.childrenComponents?.length) {
					component.childrenComponents.forEach((component) => {
						getComponentNameRecursion(component, componentsName);
					});
				}
				if (component?.blocks?.length) {
					component.blocks.forEach((component) => {
						getComponentNameRecursion(component, componentsName);
					});
				}
				//slots
				const props = Object.keys(component?.props || {});
				if (props.length) {
					props.forEach((prop) => {
						if (component.props[prop]?.componentName) {
							componentsName.push(component.props[prop].componentName);
						}
					});
				}
			});
		});
		return componentsName;
	}

	function getComponentNameRecursion(component, componentsName) {
		componentsName.push(component.componentName);
		if (component?.childrenComponents?.length) {
			component.childrenComponents.forEach((component) => {
				getComponentNameRecursion(component, componentsName);
			});
		}
		if (component?.blocks?.length) {
			component.blocks.forEach((component) => {
				getComponentNameRecursion(component, componentsName);
			});
		}
		//slots
		const props = Object.keys(component?.props || {});
		if (props.length) {
			props.forEach((prop) => {
				if (component.props[prop]?.componentName) {
					componentsName.push(component.props[prop].componentName);
					getComponentNameRecursion(component.props[prop], componentsName);
				}
			});
		}
	}

	useEffect(() => {
		if (appCss && checkoutHtml && tokens) {
			//mudar isso aqui para colocar de dependencia só componentes q foram usados
			setIsBtnDisabled(true);
			const componentList = [...new Set(getAllComponentsNameRecursive(pages))];
			//filter components, if component[].componentName is not in componentList, remove it
			const usedComps = components.filter((comp) => componentList.includes(comp.componentName));
			const manifest = Object.assign(
				usedComps.reduce(
					(prev, curr) => {
						if (!curr?.vtexApp) return prev;
						let apps = [];
						if (Array.isArray(curr.vtexApp)) {
							apps = curr.vtexApp;
						} else {
							apps = [curr.vtexApp];
						}
						apps.forEach((vtexApp) => {
							const [key, value] = vtexApp.replace(/"/g, '').split(':');
							if (!key || !value) return;
							if (Object.keys(prev).indexOf(key) === -1) {
								if (key.includes('peer')) {
									prev.peerDependencies[key.replace('peer/', '')] = value.trim();
								} else {
									prev.dependencies[key] = value.trim();
								}
							}
						});

						return prev;
					},
					{
						scripts: { postreleasy: 'vtex publish --verbose' },
						dependencies: {},
						peerDependencies: {},
						vendor: accountName,
					},
				),
				{ 'vtex.order-placed': '2.x' },
			);
			const newSass = JSON.parse(JSON.stringify(appCss));
			Object.assign(newSass, { tokens });
			fetch(`${process.env.REACT_APP_COMPILER_URL}/api/tachyons/compileJSON/`, {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
					...getAuthHeader(),
				},
				body: JSON.stringify({
					sass: JSON.stringify(newSass),
					pages: JSON.stringify(finalPages),
					viewId: viewId,
					manifest: JSON.stringify(manifest),
					checkoutHtml: JSON.stringify(checkoutHtml),
					checkoutCustom: checkoutCustom,
				}),
			})
				.then((resp) => {
					return resp.blob();
				})
				.then((blob) => {
					saveAs(blob, `${accountName || viewId}-${getNowDate()}-store.zip`);
					//limpando token pra evitar que ao salvar o arquivo, ele tente baixar novamente o zip
					//como ele tá como dependencia p chamar a função que salva o arquivo, e o state token é um obj, toda vez que salva ele identifica alteração no token
					setTokens(null);
					setIsBtnDisabled(false);
				})
				.catch((e) => {
					console.warn(e);
					toast({
						title: 'Houve um erro ao tentar compilar o projeto.',
						status: 'error',
						duration: 9000,
						isClosable: true,
					});
				})
				.finally(() => {
					setIsBtnDisabled(false);
					onClose();
					setTypeOfCheckout();
				});
			// })
		}
	}, [appCss, tokens]);

	return (
		<>
			<Button
				onClick={() => {
					compileProject();
					onOpen();
				}}
				disabled={isBtnDisabled}
				borderColor="gray.200"
				lineHeight={6}
				m={0}
				h={'auto'}
				fontSize={'xs'}
				rightIcon={<UilArrowToBottom size={12} color="gray.700" />}
				variant="outline"
			>
				Download do Projeto
			</Button>
			<Modal isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent fontFamily={'Poppins'}>
					<ModalHeader>Qual checkout você irá utilizar?</ModalHeader>
					<ModalBody fontSize={'xs'} color="gray.500">
						*As lojas da EazyGo usam o checkout clássico por padrão
					</ModalBody>

					<ModalFooter>
						<Button
							isLoading={isBtnDisabled}
							fontFamily={'Poppins'}
							fontSize={'sm'}
							colorScheme="orange"
							mr={3}
							onClick={() => {
								setTypeOfCheckout('default');
							}}
						>
							Checkout clássico
						</Button>
						<Button
							isLoading={isBtnDisabled}
							fontFamily={'Poppins'}
							fontSize={'sm'}
							colorScheme="purple"
							onClick={() => {
								setTypeOfCheckout('checkout-ui-custom');
							}}
						>
							Checkout com o app UI settings
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

export default SendToCompileBtn;
